/**
 * CatalogPicker — Autocomplete pour ajouter des postes du catalogue à un devis.
 * Extrait de QuoteEditorPage.jsx (Phase 3b refactor).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { BookOpen, Search } from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";

export default function CatalogPicker({ items, onPick, defaultSection }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items.slice(0, 20);
    return items
      .filter(
        (it) =>
          it.label.toLowerCase().includes(s) ||
          (it.pro_description || "").toLowerCase().includes(s) ||
          (it.section || "").toLowerCase().includes(s),
      )
      .slice(0, 30);
  }, [q, items]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={
            defaultSection
              ? `Rechercher un poste du catalogue (${defaultSection})…`
              : "Rechercher un poste du catalogue…"
          }
          className="border-slate-700 bg-slate-950/40 pl-9 text-slate-100"
          data-testid="catalog-picker-input"
        />
      </div>
      {open && filtered.length > 0 && (
        <div
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded border border-slate-700 bg-slate-900 shadow-xl"
          data-testid="catalog-picker-dropdown"
        >
          {filtered.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => {
                onPick(it);
                setQ("");
                setOpen(false);
              }}
              className="flex w-full items-start gap-2 border-b border-slate-800 px-3 py-2 text-left last:border-b-0 hover:bg-slate-800/60"
              data-testid={`catalog-picker-option-${it.id}`}
            >
              <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#E9A98F]" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-slate-100">{it.label}</div>
                <div className="truncate text-xs text-slate-500">
                  {it.unit_price != null ? formatCurrencyBE(it.unit_price) : "prix libre"} · {it.unit} · TVA {it.default_vat_rate}%
                  {it.section && <span className="ml-1 text-[#B8C784]"> · {it.section}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
